<template>
  <div class="hw-timeline card p-2 mb-3">
    <svg width="100%" :viewBox="`0 0 ${W} ${height}`" role="img" aria-label="Homework timeline">
      <!-- drawn first, so it stays under the axis and every dot -->
      <line v-if="nowX !== null" :x1="nowX" :x2="nowX" y1="0" :y2="axisY + 28" class="tl-now"/>
      <text v-if="nowX !== null" :x="nowX" :y="axisY + 38" class="tl-now-label">{{ nowLabel }}</text>
      <line :x1="PAD" :x2="W - PAD" :y1="axisY" :y2="axisY" class="tl-axis"/>
      <g v-for="m in monthTicks" :key="m.key">
        <line :x1="m.x" :x2="m.x" :y1="axisY - 4" :y2="axisY + 4" class="tl-axis"/>
        <text :x="m.x" :y="height - 4" class="tl-month">{{ m.label }}</text>
      </g>
      <g v-for="d in placedDots" :key="d.key" class="tl-dot-group">
        <circle :cx="d.x" :cy="d.y" :r="d.r" :style="{ fill: d.fill, stroke: d.stroke }" stroke-width="2">
          <title>{{ d.tooltip }}</title>
        </circle>
        <text :x="d.x" :y="d.y - d.r - 3" class="tl-label">{{ d.label }}</text>
        <text v-if="d.sub" :x="d.x" :y="d.y + d.r + 10" class="tl-sub">{{ d.sub }}</text>
      </g>
    </svg>
    <div class="tl-legend small text-muted">
      <template v-if="role === 'teacher'">
        <span><i class="tl-dot" :style="{ background: teacherOpenColor }"></i>Homework (open)</span>
        <span><i class="tl-dot" :style="{ background: teacherClosedColor }"></i>Homework (closed)</span>
        <span><i class="tl-dot tl-dot--sm" style="background:#adb5bd"></i>Evaluation (pre / sub / post)</span>
      </template>
      <template v-else>
        <span v-for="(s, key) in legendStates" :key="key">
          <i class="tl-dot tl-dot--sm" :style="{ background: s.color, borderColor: s.ring }"></i>{{ s.legend || s.label }}
        </span>
        <span>Large dot = homework status</span>
      </template>
    </div>
  </div>
</template>

<script>
import { DEADLINE_STATES } from '@/utils/deadlineState';

const TZ = 'Europe/Prague';
const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});
const MONTH_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, month: 'short' });
const TODAY_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: 'numeric', month: 'short' });
const HOUR_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', hour12: false });
const DAY = 24 * 3600 * 1000;
// Calendar day in Prague as YYYY-MM-DD: dots of the same day are stacked
const DAY_KEY_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

// Short phase label shown below each deadline dot (evaluation_type → display label)
const PHASE_LABELS = { pre: 'pre', submission: 'sub', post: 'post' };

// Teacher dot colours, shared between the legend and homeworkDot()
const TEACHER_OPEN_COLOR   = '#3d0f6b';
const TEACHER_CLOSED_COLOR = '#6c757d';

const HW_STATUS_COLORS = {
  unreleased: { fill: 'white',               stroke: 'var(--bs-primary)', text: 'Unreleased' },
  open:       { fill: 'var(--bs-warning)',   stroke: 'white',             text: 'Open' },
  passed:     { fill: 'var(--bs-success)',   stroke: 'white',             text: 'Passed' },
  failed:     { fill: 'var(--bs-danger)',    stroke: 'white',             text: 'Failed' },
  closed:     { fill: 'var(--bs-secondary)', stroke: 'white',             text: 'Closed' },
};

// UTC timestamp for Prague midnight of the 1st of a given month (Prague is UTC+1 or UTC+2)
function pragueMidnight(year, month) {
  const utcMidnight = Date.UTC(year, month - 1, 1);
  for (const h of [1, 2]) {
    const ts = utcMidnight - h * 3600000;
    if (HOUR_FMT.format(new Date(ts)) === '00') return ts;
  }
  return utcMidnight;
}

// Presentational only: no API calls. Dots of the same calendar day are stacked; when two
// homeworks share a release day, the second release moves up one lane rather than staying on
// the axis. There are no links, so an unreleased homework cannot be opened from here.
export default {
  name: 'HomeworkTimeline',
  props: {
    items:      { type: Array,  required: true },       // HomeworkTimelineItem[]
    role:       { type: String, default: 'student' },   // 'teacher' | 'student'
    statusById: { type: Object, default: () => ({}) },  // homework_id -> hw_status
    statsById:  { type: Object, default: () => ({}) },  // homework_id -> HomeworkTeacherStats
  },
  data() {
    // W is the logical width for xOf(); the SVG itself scales to 100 % of the container.
    // LANE_H leaves room for the number above a dot and the pre/sub/post label below the one
    // stacked over it (homework dot r=8 + 2 text lines), so stacked labels never overlap
    return { W: 1000, PAD: 40, LANE_H: 42, TOP: 22, DEADLINE_STATES,
             teacherOpenColor: TEACHER_OPEN_COLOR, teacherClosedColor: TEACHER_CLOSED_COLOR };
  },
  computed: {
    legendStates() {
      return Object.fromEntries(Object.entries(DEADLINE_STATES).filter(([, s]) => !s.hideInLegend));
    },
    range() {
      const ts = this.items.flatMap(hw => [
        Date.parse(hw.release_date), ...hw.auto_deadlines.map(dl => Date.parse(dl.date))]);
      if (!ts.length) return null;
      return { start: Math.min(...ts) - 7 * DAY, end: Math.max(...ts) + 7 * DAY };
    },
    rawDots() {
      if (!this.range) return [];
      const dots = [];
      this.items.forEach((hw, i) => {
        const n = i + 1;   // ordinal in the semester (items come ordered by release_date)
        dots.push(this.homeworkDot(hw, n));
        hw.auto_deadlines.forEach(dl => dots.push(this.deadlineDot(hw, dl, n)));
      });
      return dots.sort((a, b) => a.x - b.x);
    },
    laneDots() {
      // Dots of different days stay on the axis even when close; dots of the same day are
      // stacked: the homework release first (on the axis), then that day's evaluations above it.
      const nextLane = {};   // day -> next free lane
      const place = d => {
        const lane = nextLane[d.day] || 0;
        nextLane[d.day] = lane + 1;
        return { ...d, lane };
      };
      const homeworks = this.rawDots.filter(d => d.kind === 'homework').map(place);
      const deadlines = this.rawDots.filter(d => d.kind === 'deadline').map(place);
      return [...homeworks, ...deadlines];
    },
    laneCount() { return Math.max(1, ...this.laneDots.map(d => d.lane + 1)); },
    axisY()     { return this.TOP + (this.laneCount - 1) * this.LANE_H; },
    height()    { return this.axisY + 56; },
    placedDots() { return this.laneDots.map(d => ({ ...d, y: this.axisY - d.lane * this.LANE_H })); },
    nowX() {
      const now = Date.now();
      if (!this.range || now < this.range.start || now > this.range.end) return null;
      return this.xOf(now);
    },
    nowLabel() { return `Today · ${TODAY_FMT.format(new Date())}`; },
    monthTicks() {
      if (!this.range) return [];
      const ticks = [];
      const d = new Date(this.range.start);
      d.setUTCDate(1);
      d.setUTCMonth(d.getUTCMonth() + 1);
      for (; d.getTime() <= this.range.end; d.setUTCMonth(d.getUTCMonth() + 1)) {
        // Place the tick at Prague midnight of the 1st, not UTC midnight
        const ts = pragueMidnight(d.getUTCFullYear(), d.getUTCMonth() + 1);
        ticks.push({ key: d.toISOString(), x: this.xOf(ts), label: MONTH_FMT.format(new Date(ts)) });
      }
      return ticks;
    },
  },
  methods: {
    dayOf(iso) {
      return DAY_KEY_FMT.format(new Date(iso));
    },
    xOf(ts) {
      const { start, end } = this.range;
      return this.PAD + ((ts - start) / (end - start)) * (this.W - 2 * this.PAD);
    },
    homeworkDot(hw, n) {
      const base = { key: `hw-${hw.homework_id}`, kind: 'homework', day: this.dayOf(hw.release_date),
                     x: this.xOf(Date.parse(hw.release_date)), r: 8, label: `${n}` };
      const released = `Released ${DATE_FMT.format(new Date(hw.release_date))}`;
      if (this.role === 'teacher') {
        const s = this.statsById[hw.homework_id];
        return {
          ...base,
          fill: hw.is_closed ? TEACHER_CLOSED_COLOR : TEACHER_OPEN_COLOR, stroke: 'white',
          sub: s ? `${s.students_submitted_count}/${s.students_total_count}` : '',
          tooltip: `${n}. ${hw.title}\n${released}\n${hw.is_closed ? 'Closed' : 'Open'}`,
        };
      }
      const c = HW_STATUS_COLORS[this.statusById[hw.homework_id]]
        || (hw.is_closed ? HW_STATUS_COLORS.closed : HW_STATUS_COLORS.open);
      return { ...base, fill: c.fill, stroke: c.stroke, sub: '', tooltip: `${n}. ${hw.title}\n${released}\n${c.text}` };
    },
    deadlineDot(hw, dl, n) {
      const c = this.role === 'teacher'
        ? { color: '#adb5bd', ring: 'white', label: '' }
        : (DEADLINE_STATES[dl.state] || DEADLINE_STATES.upcoming);
      const phase = PHASE_LABELS[dl.evaluation_type] || dl.evaluation_type;
      return {
        key: `dl-${dl.deadline_id}`, kind: 'deadline', day: this.dayOf(dl.date), x: this.xOf(Date.parse(dl.date)), r: 5,
        fill: c.color, stroke: c.ring, label: `${n}`, sub: phase,
        tooltip: `${n}. ${hw.title} · ${phase}\n${DATE_FMT.format(new Date(dl.date))}`
          + (c.label ? `\n${c.label}` : ''),
      };
    },
  },
};
</script>

<style scoped>
.tl-axis  { stroke: #ced4da; stroke-width: 1.5; }
.tl-now   { stroke: #dee2e6; stroke-width: 1; stroke-dasharray: 4 3; }
.tl-now-label { font-size: 10px; fill: #adb5bd; text-anchor: middle; }
.tl-label { font-size: 10px; font-weight: 600; fill: #495057; text-anchor: middle; }
.tl-sub   { font-size: 9px; fill: #6c757d; text-anchor: middle; }
.tl-month { font-size: 10px; fill: #868e96; text-anchor: middle; }
.tl-legend { display: flex; flex-wrap: wrap; gap: 12px; padding: 4px 8px; }
.tl-dot { display: inline-block; width: 12px; height: 12px; border-radius: 50%;
          border: 2px solid white; margin-right: 4px; vertical-align: -2px; }
.tl-dot--sm { width: 9px; height: 9px; }
</style>
